import { useCallback, useEffect, useRef } from "react";
import {
  Node,
  NodeProps,
  NodeResizer,
  ResizeDragEvent,
  ResizeParams,
} from "@xyflow/react";
import { useAtomValue, useSetAtom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import {
  updateNodeAtom,
  TextNodeData,
  shouldShowStepIdsAtom,
  isViewOnlyModeAtom,
  saveNodeOnResizeEndAtom,
  canvasIdAtom,
  createNotificationAtom,
  saveCanvasAsyncCommand,
} from "@zapier/canvas-state";
import {
  ColorOrTransparent,
  RichTextEditor,
  RichTextEditorHandle,
} from "@zapier/rich-text-editor";
import type { RemirrorJSON } from "@zapier/rich-text-editor";

import { StepId } from "../../../StepId";
import { nodeResizerHandleStyle, nodeResizerLineStyle } from "../constants";

const DEFAULT_WIDTH = 290; // This is .rich-text-editor min-width + 40px padding
const DEFAULT_HEIGHT = 90; // This is .rich-text-editor min-height + 40px padding

export function TextNode(props: NodeProps<Node<TextNodeData>>) {
  const {
    id,
    selected,
    data: { label, backgroundColor },
  } = props;

  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const isEditable = !isViewOnlyMode && selected;
  const updateNode = useSetAtom(updateNodeAtom);
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const saveToBackend = useSetAtom(saveCanvasAsyncCommand);
  const saveOnResizeEnd = useSetAtom(saveNodeOnResizeEndAtom);
  const canvasId = useAtomValue(canvasIdAtom);

  const editorRef = useRef<RichTextEditorHandle>(null);

  useEffect(() => {
    /** Need the setTimeout or else the focus won't work due to
     * the fact the editor isn't fully mounted and rendered */
    if (selected) {
      setTimeout(() => {
        editorRef.current?.focus(); // Imperatively focus the editor after creating a new text node
      }, 0);
    }
  }, [selected]);

  const handleResizeEnd = useCallback(
    (_e: ResizeDragEvent, { width, height, x, y }: ResizeParams) => {
      saveOnResizeEnd({ id, width, height, x, y });
    },
    [saveOnResizeEnd, id],
  );

  const onBlur = useCallback(
    async (json: RemirrorJSON) => {
      const updatedNodes = updateNode({ id, data: { label: json } });
      saveToBackend({ nodes: updatedNodes });
    },
    [id, saveToBackend, updateNode],
  );

  const onBackgroundColorChange = useCallback(
    async (backgroundColor: ColorOrTransparent) => {
      const updatedNodes = updateNode({
        id,
        style: {
          backgroundColor,
        },
        data: {
          backgroundColor,
        },
      });

      saveToBackend({ nodes: updatedNodes });
    },
    [id, saveToBackend, updateNode],
  );

  const handleImageUploadError = useAtomCallback(
    useCallback((_get, set, message?: string) => {
      set(createNotificationAtom, {
        type: "error",
        message:
          message ||
          "We ran into an issue uploading your image. Please try again.",
      });
    }, []),
  );

  return (
    <>
      {shouldShowStepIds && <StepId />}
      {!isViewOnlyMode && (
        <NodeResizer
          minWidth={DEFAULT_WIDTH}
          minHeight={DEFAULT_HEIGHT}
          handleStyle={nodeResizerHandleStyle}
          lineStyle={nodeResizerLineStyle}
          isVisible={selected}
          onResizeEnd={handleResizeEnd}
        />
      )}
      <RichTextEditor
        canvasId={canvasId}
        nodeId={id}
        ref={editorRef}
        placeholder="Add text"
        initialContent={label}
        onBlur={onBlur}
        onBackgroundColorChange={onBackgroundColorChange}
        backgroundColor={
          (backgroundColor ?? "transparent") as ColorOrTransparent
        }
        editable={!isViewOnlyMode}
        isToolbarVisible={isEditable}
        onImageUploadError={handleImageUploadError}
      />
    </>
  );
}
